/**
 * Service Offerings API Client
 *
 * GW-3056: Client for gw-services-api service offering endpoints.
 * Base URL: services.{env}.generalwisdom.com
 * All requests include Accept-Version: 1.0 header via ServicesApiClient.
 */

import { assertOrgId } from '@/types/orgId';
import type {
  CreateServiceOfferingRequest,
  ServiceOffering,
  ServiceOfferingListResponse,
  ServiceOfferingStatus,
  ServiceTOSAcceptanceStatus,
  ServiceTOSAcceptResponse,
  UpdateServiceOfferingRequest,
} from '@/types/service-offering.types';
// GW-6533: Validate orgId at API client boundary so frontend regressions
// throw a distinct InvalidOrgIdError before the network call leaves the
// browser, instead of producing a noisy 4xx after a round-trip.

import { ServicesApiClient } from './servicesApiClient';

class ServiceOfferingsAPI extends ServicesApiClient {
  /**
   * List service offerings available in the marketplace.
   * GET /v1/services
   */
  async listMarketplace(params?: {
    limit?: number;
    cursor?: string;
    category?: string;
  }): Promise<ServiceOfferingListResponse> {
    const qs = params ? this.buildQueryString(params) : '';
    return this.get<ServiceOfferingListResponse>(`/services${qs}`);
  }

  /**
   * Get a single service offering by slug.
   * GET /services/:slug
   *
   * GW-10374: This endpoint is public at the API Gateway level (no authorizer)
   * — backend GW-10373 returns 200 for any public offering regardless of
   * status, 404 only for private/missing. The request is therefore
   * unauthenticated by default (`skipAuth`), so the marketplace detail page can
   * read it while logged out and a 403 can never emit `session-expired` and log
   * the user out (the GW-10367 bug).
   *
   * The `authenticated` option restores the Authorization header for callers
   * that must read the org's OWN, possibly PRIVATE, offering — e.g.
   * ServiceWizardPage edit mode, which would otherwise get a 404 for a private
   * offering under GW-10373.
   */
  async getBySlug(slug: string, options?: { authenticated?: boolean }): Promise<ServiceOffering> {
    return this.get<ServiceOffering>(`/services/${slug}`, {
      skipAuth: !options?.authenticated,
    });
  }

  /**
   * List service offerings for an organization.
   * GET /organizations/:orgId/services
   */
  async listOrgOfferings(
    orgId: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<ServiceOfferingListResponse> {
    assertOrgId(orgId);
    const qs = params ? this.buildQueryString(params) : '';
    return this.get<ServiceOfferingListResponse>(`/organizations/${orgId}/services${qs}`);
  }

  /**
   * Create a service offering for an organization.
   * POST /organizations/:orgId/services
   */
  async createOffering(
    orgId: string,
    data: CreateServiceOfferingRequest
  ): Promise<ServiceOffering> {
    assertOrgId(orgId);
    return this.post<ServiceOffering>(`/organizations/${orgId}/services`, data);
  }

  /**
   * Update a service offering.
   * PUT /organizations/:orgId/services/:id
   */
  async updateOffering(
    orgId: string,
    offeringId: string,
    data: UpdateServiceOfferingRequest
  ): Promise<ServiceOffering> {
    assertOrgId(orgId);
    return this.put<ServiceOffering>(`/organizations/${orgId}/services/${offeringId}`, data);
  }

  /**
   * Set the status of a service offering.
   * PUT /organizations/:orgId/services/:id/status
   */
  async setStatus(
    orgId: string,
    offeringId: string,
    status: ServiceOfferingStatus
  ): Promise<ServiceOffering> {
    assertOrgId(orgId);
    return this.put<ServiceOffering>(`/organizations/${orgId}/services/${offeringId}/status`, {
      status,
    });
  }

  /**
   * Update the visibility of a service offering via a full PUT.
   * Accepts the current offering to avoid an extra round-trip fetch.
   * PUT /organizations/:orgId/services/:id
   *
   * The backend rejects any request that includes `slug` at all (even
   * unchanged) with 400 "slug is immutable", so the payload is built
   * field-by-field rather than spreading `current` — the same pattern
   * ServiceWizardPage uses for its own update calls.
   */
  async setVisibility(
    orgId: string,
    current: ServiceOffering,
    visibility: 'public' | 'private'
  ): Promise<ServiceOffering> {
    assertOrgId(orgId);
    const payload: UpdateServiceOfferingRequest = {
      orgId: current.orgId,
      orgName: current.orgName,
      title: current.title,
      description: current.description,
      categories: current.categories ?? undefined,
      capabilities: current.capabilities ?? undefined,
      assignedConsultantUserIds: current.assignedConsultantUserIds ?? undefined,
      deliverableTemplates: current.deliverableTemplates ?? undefined,
      tokenPrice: current.tokenPrice,
      estimatedDaysMin: current.estimatedDaysMin,
      estimatedDaysMax: current.estimatedDaysMax,
      slaTerms: current.slaTerms ?? null,
      iconS3Key: current.iconS3Key ?? null,
      tosContent: current.tosContent ?? null,
      tosVersion: current.tosVersion ?? null,
      isAnonymous: current.isAnonymous,
      visibility,
    };
    return this.updateOffering(orgId, current.offeringId, payload);
  }

  /**
   * Archive a service offering (soft delete).
   * DELETE /organizations/:orgId/services/:id
   */
  async archiveOffering(orgId: string, offeringId: string): Promise<void> {
    assertOrgId(orgId);
    return this.delete<void>(`/organizations/${orgId}/services/${offeringId}`);
  }

  /**
   * Assign consultants to a service offering.
   * POST /organizations/:orgId/services/:id/consultants
   */
  async assignConsultant(
    orgId: string,
    offeringId: string,
    consultantUserIds: string[]
  ): Promise<ServiceOffering> {
    assertOrgId(orgId);
    return this.post<ServiceOffering>(
      `/organizations/${orgId}/services/${offeringId}/consultants`,
      { consultantUserIds }
    );
  }

  /**
   * Remove a consultant from a service offering.
   * DELETE /organizations/:orgId/services/:id/consultants/:userId
   */
  async removeConsultant(orgId: string, offeringId: string, userId: string): Promise<void> {
    assertOrgId(orgId);
    return this.delete<void>(
      `/organizations/${orgId}/services/${offeringId}/consultants/${userId}`
    );
  }

  /**
   * Add organization access to a service offering.
   * POST /v1/services/:id/access
   */
  async addAccess(offeringId: string, orgId: string): Promise<void> {
    assertOrgId(orgId);
    return this.post<void>(`/services/${offeringId}/access`, { orgId });
  }

  /**
   * Remove organization access from a service offering.
   * DELETE /v1/services/:id/access/:orgId
   */
  async removeAccess(offeringId: string, orgId: string): Promise<void> {
    assertOrgId(orgId);
    return this.delete<void>(`/services/${offeringId}/access/${orgId}`);
  }

  /**
   * Update audience grants for a service offering.
   * PUT /v1/services/:id with nested audience object
   */
  async updateGrants(offeringId: string, grants: string[]): Promise<ServiceOffering> {
    return this.put<ServiceOffering>(`/services/${offeringId}`, {
      audience: {
        type: 'GRANT_REQUIRED',
        grants: grants.map((name) => ({ name })),
      },
    });
  }

  // ============================================================================
  // TOS (Terms of Service) APIs
  //
  // GW-11445 (merged): confirmed against gw-services-api's
  // `functions/api-proxy/proxy/service_offerings_tos.go` and its Pact
  // consumer contract (`pact/consumers/service-offerings-tos.pact.spec.ts`).
  // Paths were already correct pre-reconciliation; the accept endpoints'
  // return types were not (they return the same TOSAcceptResponse body
  // applications/datasets/missions do, not void).
  // ============================================================================

  /**
   * Check TOS acceptance status for a service offering.
   * GET /services/{offeringId}/tos/acceptance
   */
  async checkTOSAcceptance(offeringId: string): Promise<ServiceTOSAcceptanceStatus> {
    return this.get<ServiceTOSAcceptanceStatus>(`/services/${offeringId}/tos/acceptance`);
  }

  /**
   * Accept TOS for a service offering as the current user.
   * POST /services/{offeringId}/tos/accept/user
   */
  async acceptTOSUser(
    offeringId: string,
    data: { tosVersion: number }
  ): Promise<ServiceTOSAcceptResponse> {
    return this.post<ServiceTOSAcceptResponse>(`/services/${offeringId}/tos/accept/user`, data);
  }

  /**
   * Accept TOS for a service offering on behalf of the organization.
   * POST /services/{offeringId}/tos/accept/org
   * Authorization: org_admin role
   */
  async acceptTOSOrg(
    offeringId: string,
    data: { tosVersion: number }
  ): Promise<ServiceTOSAcceptResponse> {
    return this.post<ServiceTOSAcceptResponse>(`/services/${offeringId}/tos/accept/org`, data);
  }

  /**
   * GW-11493: FR-29 cross-offering org TOS overview (org_admin/global_admin only).
   * GET /services/tos/org-overview
   * Mirrors gw-services-api's `TOSOrgOverviewResponse`
   * (`functions/api-proxy/proxy/service_offerings_tos.go`) — the same
   * summary/items envelope as applications' `ApplicationOrgTOSOverviewResponse`,
   * but with itemId/itemName rather than appId/appName/category/iconUrl.
   */
  async getOrgTOSOverview(): Promise<ServiceOrgTOSOverviewResponse> {
    return this.get<ServiceOrgTOSOverviewResponse>('/services/tos/org-overview');
  }
}

/**
 * GET /services/tos/org-overview response (GW-11493, FR-29).
 * Defined locally (mirroring `DatasetOrgTOSOverviewResponse` in
 * `api/datasets.ts`) since this cross-item read shape is not yet part of
 * `@/types/service-offering.types`.
 */
export interface ServiceOrgTOSOverviewResponse {
  items: Array<{
    itemId: string;
    itemName: string;
    tosRequiredLevel: ServiceTOSAcceptanceStatus['tosRequiredLevel'];
    tosVersion: number | null;
    orgAccepted: boolean;
    orgNeedsReaccept: boolean;
  }>;
  summary: {
    total: number;
    accepted: number;
    pending: number;
    needsReaccept: number;
  };
}

export const serviceOfferingsApi = new ServiceOfferingsAPI();
